/**
 * The top bar: breadcrumb, the reserved environment slot, and the right hand
 * cluster of search, help and the account avatar.
 *
 * The breadcrumb is account / project / environment. V1 renders nothing in the
 * environment slot: not a disabled pill, not a greyed chip. An inert control
 * violates the wayfinding rule that a disabled control sits flat because it
 * could be enabled, and it would get poked. The slot is reserved in the grid,
 * not occupied.
 *
 * WHAT CHANGED IN THE FIRST RUN PASS, and why.
 *
 * Search was a keyboard shortcut and nothing else. Screen spec 2.6 puts the
 * palette behind Command K and stops there, which is correct for Priya and
 * invisible to Nina: a person who has never used a console does not know the
 * shortcut exists, so for them the product had no search at all. It is a field
 * shaped button now, with the shortcut printed inside it, which is how a
 * person learns the shortcut instead of being expected to arrive knowing it.
 *
 * Documentation was a bare span. It was not a link, it went nowhere, and it
 * was the only help affordance in the entire product. It is now one item
 * inside a help menu that also carries the keyboard shortcuts, a way to replay
 * the tour, and the way to reach a person, which is the thing a first time
 * builder in Bangkok actually needs from this corner of the screen.
 *
 * The tour and the palette are mounted here because both of them point at, or
 * are reachable from, every screen inside the shell, and the top bar is the
 * one component that is on every one of those screens. Neither depends on that
 * choice: the tour finds its anchors by attribute, the palette reads the store.
 */

import { useEffect, useState } from 'react';

import { CommandPalette } from '../patterns/CommandPalette';
import { SpotlightTour } from '../patterns/SpotlightTour';
import { AccountPopover } from './AccountPopover';
import { HelpMenu } from './HelpMenu';
import { MobileMenu } from './MobileMenu';
import { t } from '../../lib/i18n/t';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAccount, useProjects } from '../../lib/auth/use-app-data';
import '../../styles/components/omnibar.css';
import '../../styles/components/account-popover.css';

interface TopBarProps {
  slug: string | null;
}

/**
 * Which modifier this keyboard has. It is printed rather than guessed at by
 * the person: a Windows user told to press Command has been told nothing.
 */
function modifierLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl K';
  const mac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  return mac ? '⌘K' : 'Ctrl K';
}

export function TopBar({ slug }: TopBarProps) {
  const account = useAccount();
  const projects = useProjects();
  const [advanced] = useAdvanced();
  const current = slug ? projects.find((p) => p.slug === slug) : undefined;

  const [paletteOpen, setPaletteOpen] = useState(false);

  // Command K and Control K only. No bare letter, ever: the demo dock already
  // learned that lesson with D and has to skip inputs by hand, and a search
  // that eats a keystroke somebody meant for a field is worse than no search.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'k' && event.key !== 'K') return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      setPaletteOpen(true);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <header className="topbar">
        {/* Phones only: the rail is hidden under 900px and this is its door. */}
        <MobileMenu slug={slug} />
        <div className="topbar__crumbs">
          <span className="topbar__crumb">{account.name}</span>
          {current ? (
            <>
              <span className="topbar__sep" aria-hidden="true">
                /
              </span>
              <span className="topbar__crumb topbar__crumb--current">{current.name}</span>
            </>
          ) : null}
          <span className="topbar__env-slot" aria-hidden="true" />
        </div>

        <div className="topbar__right">
          {advanced ? <span className="topbar__chip">{t('ADVANCED')}</span> : null}

          <div className="omni">
            <button
              type="button"
              className="omni__search"
              data-tour="search"
              aria-label={t('Search')}
              aria-haspopup="dialog"
              onClick={() => setPaletteOpen(true)}
            >
              <span className="omni__glass" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="13" height="13" focusable="false">
                  <path
                    d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9ZM10.4 10.4 13.5 13.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span className="omni__search-label" aria-hidden="true">
                {t('Search')}
              </span>
              <span className="omni__keys" aria-hidden="true">
                {modifierLabel()}
              </span>
            </button>

            <HelpMenu />
          </div>

          <AccountPopover account={account} />
        </div>
      </header>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <SpotlightTour />
    </>
  );
}
